import { fadeIn, fadeOut } from "../utils/fade.js"

export class DialTabsInit {
  constructor(tabsContainerId = "dialTabs", display = "block") {
    this.tabsContainerId = tabsContainerId
    this.display = display
    this.isLocked = false
    this.tabsContainer = document.getElementById(tabsContainerId)

    if (this.tabsContainer) {
      this.tabButtons = this.tabsContainer.querySelectorAll(".tabs__btn")
      this.tabItems = this.tabsContainer.querySelectorAll(".tabs__item")
      this.prevButton = this.tabsContainer.querySelector(".nav_icon.prev")
      this.nextButton = this.tabsContainer.querySelector(".nav_icon.next")
      this.tabsHeadRow = this.tabsContainer.querySelector(".tabs__head-row")
      this.init()
    }
  }

  init() {
    this.addEventListeners()
    this.activateFirstTab()
    this.updateNavigationArrows()

    this.handleResize = () => {
      this.updateNavigationArrows()
      this.checkOverflow()
    }
    window.addEventListener("resize", this.handleResize)
  }

  checkOverflow() {
    if (!this.tabsHeadRow) return

    const scrollWidth = this.tabsHeadRow.scrollWidth
    const clientWidth = this.tabsHeadRow.clientWidth

    const isOverflowing = scrollWidth - clientWidth > 1 // на случай дробных значений

    this.tabsHeadRow.classList.toggle("overflowing", isOverflowing)
  }

  hideAllTabs() {
    this.tabItems.forEach((item) => {
      fadeOut(item, 300, () => {
        item.classList.remove("active")
      })
    })
  }

  showTab(tabId) {
    const targetItem = this.tabsContainer.querySelector(`[data-tab-content="${tabId}"]`)
    if (targetItem) {
      fadeIn(
        targetItem,
        300,
        () => {
          targetItem.classList.add("active")
          this.initVideosForTab(targetItem)
        },
        this.display,
      )
    }
  }

  changeActiveButton(tabId) {
    this.tabButtons.forEach((btn) => {
      const tabTargetID = btn.dataset.tabId?.toLowerCase()

      if (tabTargetID === tabId) {
        btn.classList.add("active")
        this.scrollToActiveButton(btn)
      } else {
        btn.classList.remove("active")
      }
    })
  }

  addEventListeners() {
    this.tabButtons.forEach((button) => {
      button.addEventListener("click", this.handleTabClick.bind(this))
    })

    if (this.prevButton) {
      this.prevButton.addEventListener("click", this.handlePrevClick.bind(this))
    }

    if (this.nextButton) {
      this.nextButton.addEventListener("click", this.handleNextClick.bind(this))
    }

    if (this.tabsHeadRow) {
      this.tabsHeadRow.addEventListener("scroll", this.updateNavigationArrows.bind(this))
    }
  }

  handleTabClick(event) {
    const targetTab = event.currentTarget.dataset.tabId?.toLowerCase()

    if (this.isLocked || event.currentTarget.classList.contains("active")) return
    this.isLocked = true

    this.hideAllTabs()
    this.showTab(targetTab)
    this.changeActiveButton(targetTab)

    setTimeout(() => {
      this.isLocked = false
    }, 300)
  }

  handlePrevClick() {
    const activeTabIndex = Array.from(this.tabItems).findIndex((item) =>
      item.classList.contains("active"),
    )
    if (activeTabIndex === 0) return
    const prevTabIndex = activeTabIndex === 0 ? this.tabItems.length - 1 : activeTabIndex - 1
    const prevTabId = this.tabButtons[prevTabIndex].dataset.tabId?.toLowerCase()

    this.changeTab(prevTabId)
  }

  handleNextClick() {
    const activeTabIndex = Array.from(this.tabItems).findIndex((item) =>
      item.classList.contains("active"),
    )
    if (activeTabIndex === this.tabItems.length - 1) return

    const nextTabIndex = activeTabIndex === this.tabItems.length - 1 ? 0 : activeTabIndex + 1
    const nextTabId = this.tabButtons[nextTabIndex].dataset.tabId?.toLowerCase()

    this.changeTab(nextTabId)
  }

  scrollToActiveButton(button) {
    const buttonRect = button.getBoundingClientRect()
    const containerRect = this.tabsHeadRow.getBoundingClientRect()

    const buttonCenter = buttonRect.left + buttonRect.width / 2
    const containerCenter = containerRect.left + containerRect.width / 2

    const scrollAmount = buttonCenter - containerCenter

    this.tabsHeadRow.scrollBy({
      left: scrollAmount,
      behavior: "smooth",
    })
  }

  changeTab(tabId) {
    if (this.isLocked) return
    this.isLocked = true

    this.hideAllTabs()
    this.showTab(tabId)
    this.changeActiveButton(tabId)

    setTimeout(() => {
      this.isLocked = false
    }, 300)
  }

  activateFirstTab() {
    const firstButton = this.tabButtons[0]
    const firstTabId = firstButton.dataset.tabId?.toLowerCase()

    if (firstTabId) {
      firstButton.classList.add("active")
      this.showTab(firstTabId)
      this.scrollToActiveButton(firstButton)
      const firstTab = this.tabsContainer.querySelector(`[data-tab-content="${firstTabId}"]`)
      if (firstTab) {
        this.initVideosForTab(firstTab)
      }
    }
  }

  updateNavigationArrows() {
    if (!this.prevButton || !this.nextButton || !this.tabsHeadRow) return

    const containerWidth = this.tabsHeadRow.offsetWidth
    const scrollWidth = this.tabsHeadRow.scrollWidth
    const scrollLeft = this.tabsHeadRow.scrollLeft

    if (scrollWidth <= containerWidth) {
      this.prevButton.style.display = "none"
      this.nextButton.style.display = "none"
      return
    }

    if (scrollLeft === 0) {
      this.prevButton.style.display = "none"
    } else {
      this.prevButton.style.display = "flex "
    }

    if (scrollLeft === scrollWidth - containerWidth) {
      this.nextButton.style.display = "none"
    } else {
      this.nextButton.style.display = "flex"
    }

    this.checkOverflow()
  }

  async initVideosForTab(tabElement) {
    const accordionItems = tabElement.querySelectorAll(".accordion__item")
    const videos = tabElement.querySelectorAll(".contact_video")

    accordionItems.forEach((item) => {
      const button = item.querySelector(".accordion__toggler")
      const videoId = item.getAttribute("data-video-id")

      button.addEventListener("click", async () => {
        const targetVideo = tabElement.querySelector(`#${videoId}`)

        for (const video of videos) {
          video.classList.remove("active")
          try {
            await video.pause()
          } catch (e) {
            // intentionally ignored
          }
        }

        if (targetVideo) {
          targetVideo.classList.add("active")
          targetVideo.currentTime = 0
          try {
            await targetVideo.play()
          } catch (err) {
            console.warn("Play interrupted or blocked:", err)
          }
        }
      })
    })

    const firstItem = accordionItems[0]
    if (firstItem) {
      const firstVideoId = firstItem.getAttribute("data-video-id")
      const firstVideo = tabElement.querySelector(`#${firstVideoId}`)

      if (firstVideo) {
        for (const video of videos) {
          video.classList.remove("active")
          try {
            await video.pause()
          } catch (e) {
            // intentionally ignored
          }
        }

        firstVideo.classList.add("active")
        firstVideo.currentTime = 0
        try {
          await firstVideo.play()
        } catch (err) {
          console.warn("Initial play interrupted:", err)
        }
      }
    }
  }

  destroy() {
    window.removeEventListener("resize", this.handleResize)
  }
}
